/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/** The charge behind and the charge ahead, with the way to the full history. */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, UnstyledButton } from '@/shared/ui';
import { useMediaQuery } from '@/shared/hooks/use-media-query';
import { formatCents } from '@/shared/utils/format-cents';
import { formatLocalizedDate } from '@/shared/utils/date';
import { seatsChanged, type Charge } from './billing-dates';

/** One of the two figures. */
const Side: React.FC<{
  label: string;
  charge: Charge | null;
  empty: string;
  support?: string;
  first: boolean;
  stacked: boolean;
  testId: string;
}> = ({ label, charge, empty, support, first, stacked, testId }) => {
  const { t, i18n } = useTranslation('features/subscription');
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
        flex: '1 1 0', minWidth: 0,
        padding: stacked ? 'var(--space-3) 0' : '0 var(--space-5)',
        borderLeft: first || stacked ? 'none' : '1px solid var(--border-ultra-subtle)',
        borderTop: !first && stacked ? '1px solid var(--border-ultra-subtle)' : 'none',
      }}
    >
      <Text
        component="span"
        style={{
          fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)',
          color: 'var(--text-tertiary-soft)',
        }}
      >
        {label}
      </Text>
      <Text
        component="span"
        style={{
          fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-xl)',
          fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
          letterSpacing: '-0.01em',
        }}
      >
        {charge ? formatCents(charge.cents) : empty}
      </Text>
      <Text
        component="span"
        style={{ fontSize: 'var(--text-meta)', color: 'var(--text-secondary)' }}
      >
        {charge
          ? [
            charge.date
              ? formatLocalizedDate(charge.date, i18n.language)
              : t('v2.billing.noDate'),
            support,
          ].filter(Boolean).join(' · ')
          : ' '}
      </Text>
    </div>
  );
};

export interface BillingDatesProps {
  last: Charge | null;
  next: Charge | null;
  /** Shown beside the next date when a reduction is already scheduled. */
  reduced: boolean;
  onSeeInvoices: () => void;
  /** In the free trial the next charge is the first one. */
  trial?: boolean;
}

/**
 * Last charge and next charge, side by side.
 *
 * The page already lists past invoices, but a list answers "what happened"
 * and this answers "what is about to". Those are different questions, and the
 * second one is why an agency opens this tab after changing its team.
 *
 * @param props - The two charges, whether a reduction is pending, and the
 *   way to the full history.
 * @returns The billing block.
 */
export const BillingDates: React.FC<BillingDatesProps> = ({
  last, next, reduced, onSeeInvoices, trial = false,
}) => {
  const { t } = useTranslation('features/subscription');
  const stacked = useMediaQuery('(max-width: 768px)');

  return (
    <div
      data-testid="plan-billing-dates"
      style={{
        display: 'flex', flexDirection: 'column', gap: 'var(--space-4)',
        padding: 'var(--space-5)', borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border-subtle)', background: 'var(--surface-raised)',
      }}
    >
      <div
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <Text
          component="h3"
          style={{
            margin: 0, fontFamily: 'var(--font-family-alt-2)',
            fontSize: 'var(--text-app-section)', fontWeight: 'var(--font-bold)',
            color: 'var(--text-primary)',
          }}
        >
          {t('v2.billing.title')}
        </Text>
        <UnstyledButton
          onClick={onSeeInvoices}
          data-testid="plan-billing-see-invoices"
          style={{
            background: 'none', cursor: 'pointer', fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-semibold)', color: 'var(--link-text)',
          }}
        >
          {t('v2.billing.seeInvoices')}
        </UnstyledButton>
      </div>

      <div
        style={{
          display: 'flex', flexDirection: stacked ? 'column' : 'row',
          alignItems: stacked ? 'stretch' : 'flex-start',
        }}
      >
        <Side
          label={t('v2.billing.last')}
          charge={last}
          empty={t('v2.billing.noneYet')}
          first
          stacked={stacked}
          testId="plan-billing-last"
        />
        <Side
          label={t(trial ? 'v2.billing.first' : 'v2.billing.next')}
          charge={next}
          empty={t('v2.billing.noRenewal')}
          // Said here rather than left to the summary above: this is the
          // figure the reduction actually changes, and a reader checking what
          // they will pay should not have to infer that it already counts.
          support={reduced ? t('v2.billing.afterReduction') : undefined}
          first={false}
          stacked={stacked}
          testId="plan-billing-next"
        />
      </div>

      {seatsChanged(last, next) && (
        <Text
          component="p"
          data-testid="plan-billing-changed"
          style={{
            margin: 0, fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)',
          }}
        >
          {t('v2.billing.changed')}
        </Text>
      )}
    </div>
  );
};

export default BillingDates;
